import { TypedDocumentNode, gql } from '@apollo/client';
import { Suspense, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { animated, useSpring } from 'react-spring';
import styled from 'styled-components';

import { Orientation, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Modal } from '@sorare/core/src/atoms/Modal';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Card } from '@sorare/core/src/components/card/Card';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import useMutation from '@sorare/core/src/hooks/graphql/useMutation';
import { getCardRepresentation } from '@sorare/core/src/lib/cards';
import { glossary } from '@sorare/core/src/lib/glossary';
import { lazy } from '@sorare/core/src/lib/retry';

import CardProperties from '@sorare/marketplace/src/components/card';
import TokenStatsIcon from '@sorare/marketplace/src/components/token/TokenStatsIcon';

import { CraftingMachine } from '../CraftingMachine';
import {
  CraftingExperience_cardShardsChest,
  SpendShards,
  SpendShardsVariables,
} from './__generated__/index.graphql';

const CardInPrinter = lazy(
  async () => import('@sorare/trois/src/components/CardInPrinter')
);

const Absolute = styled.div`
  position: absolute;
  inset: 0;
  isolation: isolate;
`;

const Video = styled(animated.video)`
  position: fixed;
  inset: 0;
  object-fit: cover;
  opacity: 1;
  width: 100%;
  height: 100%;
  background: black;
`;

const StickyFooter = styled(Vertical)`
  position: absolute;
  bottom: var(--double-unit);
  right: 0;
  left: 0;
  align-items: center;
`;

const CenteredCard = styled(Vertical).attrs({ center: true })`
  justify-content: center;
  align-items: center;
  height: 100%;
  > ${Vertical} {
    width: max-content;
  }
`;

type CardInMutationReturn = NonNullable<
  NonNullable<SpendShards['spendCardShardsChest']>['cardShardsChest']
>['card'];

const MUTATION_SPEND_CARD_CHEST = gql`
  mutation SpendShards(
    $input: spendCardShardsChestInput!
    $orientation: Orientation!
    $sport: Sport!
  ) {
    spendCardShardsChest(input: $input) {
      currentUser {
        slug
        cardShardsChests(sport: $sport) {
          id
          rarity
          cardShardsCount
        }
      }
      cardShardsChest {
        id
        card {
          slug
          ...CardProperties_anyCard
          ...TokenStatsIcon_anyCard
          ...getCardRepresentation_anyCardInterface
          ...Card_anyCard
        }
        revealVideoUrl(orientation: $orientation)
      }
      errors {
        message
      }
    }
  }
  ${Card.fragments.anyCard}
  ${CardProperties.fragments.anyCard}
  ${TokenStatsIcon.fragments.anyCard}
  ${getCardRepresentation.fragments.anyCardInterface}
` as TypedDocumentNode<SpendShards, SpendShardsVariables>;

type Props = {
  cardShardsChest: CraftingExperience_cardShardsChest;
  craft?: () => SpendShards;
  onContinue?: (craftedCardSlug: Nullable<string>) => void;
};

export const CraftingExperience = ({
  cardShardsChest,
  craft,
  onContinue,
}: Props) => {
  const [mutate] = useMutation(MUTATION_SPEND_CARD_CHEST, {
    showErrorsWithSnackNotification: true,
  });

  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const { sport } = useSportContext();

  const [forgedItem, setForgedItem] = useState<
    | {
        video: string;
        card: CardInMutationReturn;
      }
    | undefined
  >(undefined);

  const [videoEnded, setVideoEnded] = useState<boolean>(false);
  const [revealEnded, setRevealEnded] = useState<boolean>(false);
  const fadeOutVideo = useSpring({
    opacity: videoEnded ? 0 : 1,
    onRest: () => {
      if (videoEnded) {
        setRevealEnded(true);
      }
    },
  });

  const reset = (full?: boolean) => {
    setVideoEnded(false);
    setRevealEnded(false);
    if (full) {
      setForgedItem(undefined);
    }
  };

  useEffect(() => {
    reset(true);
  }, [cardShardsChest.rarity]);

  const onCraft = async () => {
    const data = craft
      ? craft()
      : (
          await mutate({
            variables: {
              sport: sport || Sport.FOOTBALL,
              input: {
                cardShardsChestId: cardShardsChest.id,
              },
              orientation: isLaptopAndAbove
                ? Orientation.LANDSCAPE
                : Orientation.PORTRAIT,
            },
          })
        ).data;

    if (
      data?.spendCardShardsChest?.errors &&
      data.spendCardShardsChest.errors.length > 0
    ) {
      return null;
    }

    return () => {
      if (data?.spendCardShardsChest?.cardShardsChest) {
        setForgedItem({
          video: data.spendCardShardsChest.cardShardsChest.revealVideoUrl ?? '',
          card: data.spendCardShardsChest.cardShardsChest.card,
        });
      }
    };
  };
  const Stats = forgedItem?.card && (
    <Horizontal>
      <CardProperties card={forgedItem.card} withTransferMalus={false} />
      <TokenStatsIcon card={forgedItem.card} />
    </Horizontal>
  );
  return (
    <>
      {forgedItem ? (
        <>
          <Absolute>
            <Suspense>
              {forgedItem.card?.threeDimensionalAsset?.gltfUrl && (
                <CardInPrinter
                  card={getCardRepresentation(forgedItem.card)}
                  fallback={
                    <CenteredCard>
                      <Vertical center>
                        <Card card={forgedItem.card} />
                        {Stats}
                      </Vertical>
                    </CenteredCard>
                  }
                >
                  {Stats}
                </CardInPrinter>
              )}
            </Suspense>
          </Absolute>

          {videoEnded && (
            <StickyFooter>
              <Button
                color="primary"
                size="medium"
                onClick={() => {
                  if (onContinue) {
                    onContinue(forgedItem.card?.slug);
                  } else {
                    reset(true);
                  }
                }}
              >
                <FormattedMessage {...glossary.continue} />
              </Button>
              <Button color="secondary" size="medium" onClick={() => reset()}>
                <FormattedMessage
                  id="Essence.CraftingExperience.replayYourReveal"
                  defaultMessage="Replay reveal"
                />
              </Button>
            </StickyFooter>
          )}

          {!revealEnded && (
            <Modal>
              <Video
                autoPlay
                onEnded={() => setVideoEnded(true)}
                style={fadeOutVideo}
              >
                <source src={forgedItem.video} />
              </Video>
            </Modal>
          )}
        </>
      ) : (
        <CraftingMachine cardShardsChest={cardShardsChest} onCraft={onCraft} />
      )}
    </>
  );
};

CraftingExperience.fragments = {
  cardShardsChest: gql`
    fragment CraftingExperience_cardShardsChest on CardShardsChest {
      id
      card {
        slug
      }
      rarity
      cardShardsCount
      newThresholdUnlocked
      thresholds {
        threshold
      }
      ...CraftingMachine_cardShardsChest
    }
    ${CraftingMachine.fragments.cardShardsChest}
  ` as TypedDocumentNode<CraftingExperience_cardShardsChest>,
};
